import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getAdminSupport } from "@/lib/admin.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/system")({
  component: AdminSystemPage,
});

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function AdminSystemPage() {
  const fetchSupport = useServerFn(getAdminSupport);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-support"],
    queryFn: () => fetchSupport(),
  });

  if (isLoading || !data) {
    return (
      <div className="flex min-h-[30vh] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="rounded-3xl">
        <CardHeader>
          <CardTitle className="text-lg font-light">Support-Anfragen</CardTitle>
          <CardDescription>Die 15 neuesten Tickets</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {data.tickets.map((ticket) => (
            <div key={ticket.id} className="rounded-2xl border p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">{ticket.ticket_number}</span>
                <Badge variant={ticket.status === "open" ? "default" : "secondary"}>
                  {ticket.status}
                </Badge>
                <span className="text-xs text-muted-foreground">{ticket.category}</span>
              </div>
              <p className="mt-1 text-sm font-medium">{ticket.subject}</p>
              <p className="text-xs text-muted-foreground">
                {ticket.email} · {formatDateTime(ticket.created_at)}
              </p>
            </div>
          ))}
          {data.tickets.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Anfragen.</p>
          ) : null}
        </CardContent>
      </Card>

      <Card className="rounded-3xl">
        <CardHeader>
          <CardTitle className="text-lg font-light">Technik-Protokoll</CardTitle>
          <CardDescription>Letzte Systemmeldungen</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {data.logs.map((log) => (
            <div key={log.id} className="rounded-2xl border p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={log.level === "error" ? "destructive" : "secondary"}>
                  {log.level}
                </Badge>
                <span className="text-xs text-muted-foreground">{log.kind}</span>
                <span className="text-xs text-muted-foreground">
                  {formatDateTime(log.created_at)}
                </span>
              </div>
              <p className="mt-1 line-clamp-3 text-sm">{log.message}</p>
            </div>
          ))}
          {data.logs.length === 0 ? (
            <p className="text-sm text-muted-foreground">Keine Meldungen.</p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
